import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import Check from 'src/models/Check';
import CheckOptions from 'src/models/CheckOptions';
import useFetch from 'use-http';

interface IManageChecksDialogStateProps {
    dialogName: string;
    clientId: number;
    projectId: number;
    contractId: number;
    checksList: Check[];
    checkOptions: CheckOptions;
    formatData: (data: Check) => Check;
    hideDialog: (reset: boolean) => void;
    extendDueDate: (checkNumber: string) => void;
}

const ManageChecksDialog = (props: IManageChecksDialogStateProps) => {
    const defaultValues: Check = {
        id: 0,
        checkTypeId: 0,
        leaseId: props.contractId,
        companyId: 0,
        countyId: 0,
        invoiceTotal: 0,
        checkNumber: '',
        checkDate: null,
        totalAmount: 0,
        dateSent: null,
        glDate: null,
        voidDate: null,
        checkDueDate: null,
        isVoid: false,
        voidReason: '',
        bonusAmount: 0,
        otherAmount: 0,
        rentalsAmount: 0,
        bankFeesAmount: 0,
        payee: '',
        address: '',
        city: '',
        stateId: 0,
        stateName: '',
        zip: '',
        memo: '',
        projectNumber: 0,
        withholdingPercent: 28,
        leaseNumber: '',
        invoiceNumber: '',
        totalRemaining: 0,

        invoices: [],
        lease: null,
        recordingFees: []
    };

    const [checkData, setCheckData] = useState<Check>(defaultValues);
    const [showForm, setShowForm] = useState<boolean>(false);
    const { register, getValues, handleSubmit, reset, errors } = useForm();
    const { put, response } = useFetch();

    const onSubmit = async (check: Check) => {
        check = props.formatData(check);
        check.id = checkData?.id;

        if (check.id > 0) {
            await put(`v1.0/clients/${props.clientId}/projects/${props.projectId}/check`, check);

            if (response.ok) {
                onCloseDialog();
                props.hideDialog(true);
            }
        }
    };

    useEffect(() => {
        reset(checkData);
    }, [checkData]);

    const onCloseDialog = () => {
        setCheckData(defaultValues);
        setShowForm(false);
    };

    const displayCheckForm = () => {
        let rec = getValues();
        let index = props.checksList.indexOf(props.checksList.find(cl => cl.checkNumber === rec.checkNumber));

        setCheckData(props.checksList[index]);
        setShowForm(true);
    };

    const getNumberForExtension = () => {
        let rec = getValues();
        props.hideDialog(false);
        props.extendDueDate(rec.checkNumber);
    };

    return (
        <Dialog dialogName={props.dialogName} title={`Select Check Type`} onCloseDialog={onCloseDialog}>
            <div className="mb-1 mt-1">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="mr-2">
                        <div style={{ display: 'flex' }}>
                            <div style={{ padding: '1rem' }}>
                                <RHFSelect
                                    name="checkNumber"
                                    label="Check Number"
                                    options={props.checksList?.map((c) => {
                                        return { value: c.checkNumber, label: c.checkNumber };
                                    })}
                                    register={register}
                                    errors={errors}
                                    onChange={displayCheckForm}
                                />
                            </div>
                        </div>
                    </div>
                    {showForm && (
                        <div>
                            <div style={{ display: 'flex'}}>
                                <div style={{ padding: '1rem', width: '75%' }}>
                                    <div className="form-row">
                                        <RHFInputBox name="checkDate" label="Check Date" register={register} errors={errors} type="date" />
                                        <RHFInputBox name="totalAmount" label="Check Total" register={register} errors={errors} type="number" disabled={true} />
                                        <RHFSelect
                                            name="checkTypeId"
                                            label="Check Type"
                                            options={props.checkOptions?.checkTypes?.map((ct) => {
                                                return { value: ct.id, label: ct.name };
                                            })}
                                            register={register}
                                            errors={errors}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className="form-row">
                                        <RHFCheckbox name="isPrinted" label="Printed?" register={register} errors={errors} />
                                        <RHFCheckbox name="isVoid" label="Void?" register={register} errors={errors} />
                                    </div>
                                    <div className="form-row">
                                        <RHFInputBox name="voidReason" label="Void Reason" register={register} errors={errors} style={{ width: '100%' }} />
                                    </div>
                                    <div className="form-row">
                                        <RHFSelect
                                            name="company"
                                            label="Company"
                                            options={props.checkOptions?.companies?.map((c) => {
                                                return { value: c.id, label: c.name };
                                            })}
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>
                                <div style={{ padding: '1rem', width: '25%' }}>
                                    <div className="form-row">
                                        <RHFInputBox name="checkDueDate" label="Check Due Date" register={register} errors={errors} type="date" disabled={true} />
                                    </div>
                                    <div className="form-row">
                                        <RHFInputBox name="glDate" label="G/L Date" register={register} errors={errors} type="date" />
                                    </div>
                                    <div className="form-row">
                                        <RHFInputBox name="voidDate" label="Void Date" register={register} errors={errors} type="date" />
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex' }}>
                                <div style={{ padding: '1rem', width: '100%' }}>
                                    <div className="form-row">
                                        <RHFTextArea name="payee" label="Payee" register={register} errors={errors} style={{ width: '100%' }} />
                                    </div>
                                    <div className="form-row">
                                        <RHFInputBox name="address" label="Address" register={register} errors={errors} style={{ width: '100%' }} />
                                    </div>
                                    <div className="form-row">
                                        <RHFInputBox name="city" label="City" register={register} errors={errors} />
                                        <RHFSelect
                                            name="stateId"
                                            label="State"
                                            options={props.checkOptions?.states?.map(s => {
                                                return { value: s.id, label: s.name };
                                            })}
                                            register={register}
                                        />
                                        <RHFInputBox name="zip" label="Zip Code" register={register} errors={errors} />
                                    </div>
                                    <div className="form-row">
                                        <RHFInputBox name="memo" label="Memo" register={register} errors={errors} style={{ width: '100%' }} />
                                    </div>
                                    <div>
                                        <Button text="Save Changes" intent="primary" type="submit" style={{ padding: '1rem' }}/>
                                        <Button text="Extend Due Date" intent="primary" onClick={getNumberForExtension} style={{ padding: '1rem' }} />
                                    </div>
                                </div>
                            </div>
                        </div>  
                    )}
                </form>
            </div>
        </Dialog>
    );
};

export default ManageChecksDialog;


// WEBPACK FOOTER //
// ./src/components/invoicing/components/ManageChecksDialog.tsx